/**
 * The one place MapLibre's worker URL is set.
 *
 * `setWorkerUrl` writes module-global config shared by every map on the page, so
 * it must be set exactly once and before the first `new Map()` — after that the
 * dispatcher has already been prewarmed against whatever URL was in force.
 *
 * The URL cannot be left to MapLibre's own default: it derives one from
 * `import.meta.url` and yields an empty string under Turbopack's bundling, which
 * produces a worker that never starts and a map that never requests a tile. See
 * MAP_WORKER_URL for the full trace.
 */

import { MAP_WORKER_URL } from './map-constants';

type MapLibreModule = typeof import('maplibre-gl');

let hasSetWorkerUrl = false;

/** Idempotent: safe to call before every map, does work before the first one. */
export function ensureMapWorkerUrl(maplibre: MapLibreModule): void {
  if (hasSetWorkerUrl) {
    return;
  }

  // Guard on the library's own state too, not just the module flag — a second
  // copy of this module (route-level code splitting) must not re-set global
  // config underneath a map that is already running.
  if (maplibre.getWorkerUrl() !== MAP_WORKER_URL) {
    maplibre.setWorkerUrl(MAP_WORKER_URL);
  }

  hasSetWorkerUrl = true;
}
